import { and, or } from '@fmnts/core';
import * as Rec from 'effect/Record';
import { ConditionFn } from './condition.type';
import {
  after,
  before,
  blank,
  equals,
  hasSome,
  isConditionAfter,
  isConditionAnd,
  isConditionBefore,
  isConditionBlank,
  isConditionEquals,
  isConditionHasSome,
  isConditionMax,
  isConditionMin,
  isConditionNegate,
  isConditionOneOf,
  isConditionOr,
  isConditionTimespan,
  max,
  min,
  negate,
  oneOf,
  timespan,
} from './conditions';

/**
 * Resolves the given `condition` to a callable
 * condition function.
 *
 * @param condition Condition definition
 *
 * @returns
 * Callable condition check function
 */
export function resolveConditionFn(
  condition: Rec.ReadonlyRecord<string, any>,
): ConditionFn {
  // Resolve and/or conditions
  if (isConditionAnd(condition)) {
    return and(...condition.and.map(resolveConditionFn));
  } else if (isConditionOr(condition)) {
    return or(...condition.or.map(resolveConditionFn));
  }

  // First resolve the conditional check function
  let conditionFn = resolveAtomicConditionFn(condition);

  // Now, apply any higher order condition functions
  if (isConditionNegate(condition)) {
    conditionFn = negate(condition, conditionFn);
  }

  return conditionFn;
}

/**
 * @param condition Condition configuration
 *
 * @returns
 * The condition check function for the given `condition`
 */
function resolveAtomicConditionFn(
  condition: Rec.ReadonlyRecord<string, any>,
): ConditionFn {
  if (isConditionEquals(condition)) {
    return equals(condition);
  } else if (isConditionMax(condition)) {
    return max(condition);
  } else if (isConditionMin(condition)) {
    return min(condition);
  } else if (isConditionOneOf(condition)) {
    return oneOf(condition);
  } else if (isConditionBlank(condition)) {
    return blank(condition);
  } else if (isConditionBefore(condition)) {
    return before(condition);
  } else if (isConditionAfter(condition)) {
    return after(condition);
  } else if (isConditionTimespan(condition)) {
    return timespan(condition);
  } else if (isConditionHasSome(condition)) {
    return hasSome({
      ...condition,
      has_some: resolveConditionFn(condition.has_some),
    });
  }

  throw new Error('Could not resolve atomic condition');
}
